/**
 * Compliance-Nahtstelle ([V] E-i, E-l, E-p3, E-p12; [TP4] 8; Anhang A.compliance).
 *
 * - `ComplianceVerdict` (E-i): `classification` folgt `effectiveClass` (promotional ⇔ werblich, unclear ⇔
 *   `allow_with_notice`); `optedOut` ⇒ `deny`.
 * - `OutboundCheck`: Eingabe für `evaluate`/`reserveOutbound`; `actionKey` ohne `linkedin.post.publish`
 *   (API-Veröffentlichung läuft über den Veröffentlichungsanspruch, nicht über die manuelle Ausgangsprüfung).
 * - `ActionIdentity` (R-28): einstiegsunabhängige fachliche Identität mit Präfix der Quelle (E-p12).
 * - `deriveActionIdentity` (C-W2, Plan 2-A VB-6, ersetzt R-2): die **reine** Funktion liegt hier, nicht in
 *   `compliance/core`: TP2/TP3/TP5 dürfen `compliance` nicht importieren (Matrix) und brauchen sie parallel zu W2-01.
 *   Es gibt genau diese eine Implementierung; Domänen bilden Identitäten nie selbst (E-p12 a).
 * - `BudgetLedger` ist die einzige Schreibschnittstelle des Budget-Buchs; Buchungen fremder Benutzer →
 *   `DomainFailure('NOT_FOUND')`.
 */
import { z } from 'zod';
import type { DbTx, DomainContext, DomainResult, PersonId, UserId } from './common.js';
import type { HeldLocks } from './locks.js';
import { ActionKind, type CommunicationClass } from './tasks.js';

export type ComplianceVerdict = {
  effectiveClass: z.infer<typeof CommunicationClass>;
  permission: 'allow' | 'allow_with_notice' | 'deny';
  classification: 'non_promotional' | 'promotional' | 'unclear';
  consent: 'granted' | 'not_recorded' | 'withdrawn';
  optedOut: boolean;
  art14Due: boolean;
  reasons: string[];
  rulesVersion: string;
  complianceVersion: number;
};

export const ContentRef = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({
    kind: z.literal('draft'),
    type: z.enum(['tp3.reply_draft', 'tp2.warmup_draft', 'tp5.content_version']),
    id: z.uuid(),
    version: z.int(),
  }),
  z.object({
    kind: z.literal('template'),
    templateId: z.enum(['art14_message_v1', 'optout_ack_v1']),
    personId: z.uuid(),
  }),
  z.object({ kind: z.literal('operation'), operationId: z.uuid() }),
]);

/** Präfixe der Buchungsquellen aus E-p12; identisch mit den `candidateKey`-Präfixen der Aufgaben. */
export const ActionIdentity = z
  .string()
  .min(3)
  .max(200)
  .regex(
    /^(reply|followup|warmup|seq|comment|reaction|privacy|nurture|post|operation):[A-Za-z0-9:_-]+$/,
  );

export const OutboundCheck = z.object({
  actionKey: z.enum([
    'linkedin.message.send',
    'linkedin.invitation.send',
    'linkedin.comment.create',
    'linkedin.reaction.create',
    'linkedin.post.manual',
  ]),
  actionKind: ActionKind,
  personId: z.uuid().nullable(),
  connectedAccountId: z.uuid(),
  conversationId: z.uuid().optional(),
  content: ContentRef,
  purposeHint: z.enum(['personal_reply', 'relationship', 'promotional']).optional(),
  actionIdentity: ActionIdentity,
});
export type OutboundCheck = z.infer<typeof OutboundCheck>;

export interface DraftContentResolver {
  type: 'tp3.reply_draft' | 'tp2.warmup_draft' | 'tp5.content_version' | 'operation';
  load(
    tx: DbTx,
    ctx: DomainContext,
    id: string,
    version?: number,
  ): Promise<DomainResult<{ text: string; promotional: boolean | null; ownerUserId: UserId }>>;
}

export type LedgerHolder = { type: 'task' | 'operation' | 'handoff' | 'publication'; id: string };

export interface BudgetLedger {
  planLocks(ctx: DomainContext, a: { connectedAccountId: string }): string[];
  reserve(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: {
      actionIdentity: string;
      actionKind: z.infer<typeof ActionKind>;
      connectedAccountId: string;
      personId: PersonId | null;
      holder: LedgerHolder;
      source: { type: string; id: string };
    },
  ): Promise<DomainResult<{ ledgerId: string; reused: boolean }>>;
  /**
   * `match` (C-W2, Plan 2-C VB-1 a): Abgleichsfenster der Buchung (`match_from`/`match_to`, [TP4] 7.8.1). Der
   * **Aufrufer** berechnet es (Aufgabe T3/T6: Beginn des Plantags des Einplanungszyklus, T13: Zeitpunkt ± 48 h);
   * fehlt es, gilt die Regel „andere Halter“: Reservierungszeitpunkt − 1 h … Verbrauchszeitpunkt + 48 h. Das Fenster
   * wird beim ersten Verbrauch gespeichert und danach nicht mehr geändert (wiederholtes `consume` = No-op);
   * `from` nach `to` → `DomainFailure('INVALID_INPUT')`. Zuordnungsprädikat (beide Richtungen gleich): `from` ≤
   * Zeitpunkt der Interaktion ≤ `to` (Grenzen eingeschlossen), dazu Konto, Aktionsart und Person.
   */
  consume(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: {
      ledgerId: string;
      holder: LedgerHolder;
      interactionId?: string;
      match?: { from: Date; to: Date };
    },
  ): Promise<void>;
  release(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { ledgerId: string; holder: LedgerHolder; reason: string },
  ): Promise<void>;
  observe(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { interactionId: string },
  ): Promise<{ matchedLedgerId: string | null }>;
}

export type ActionIdentitySource =
  | { kind: 'reply'; inboundMessageId: string }
  | { kind: 'followup'; id: string; rev: number }
  | { kind: 'warmup'; stepId: string }
  | { kind: 'seq'; enrollmentId: string; stepIndex: number }
  | { kind: 'comment' | 'reaction'; radarItemId: string }
  | { kind: 'privacy'; personId: string; reopenCount: number }
  | { kind: 'nurture'; personId: string; quarter: string }
  | { kind: 'post'; contentItemId: string }
  | { kind: 'operation'; operationId: string };
export type DeriveActionIdentity = (src: ActionIdentitySource) => string;

/** Bestandteil einer Identität: ID oder Zähler ohne Doppelpunkt (sonst mehrdeutig), 1–100 Zeichen. */
const IDENTITY_TOKEN = /^[A-Za-z0-9_-]{1,100}$/;
const IDENTITY_QUARTER = /^\d{4}-Q[1-4]$/;

function part(value: string, what: string): string {
  if (!IDENTITY_TOKEN.test(value)) {
    throw new RangeError(`deriveActionIdentity: ungültiger Bestandteil (${what})`);
  }
  return value;
}

function count(value: number, what: string): string {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`deriveActionIdentity: ungültiger Zähler (${what})`);
  }
  return String(value);
}

/**
 * Die einzige Bildungsvorschrift der fachlichen Aktionsidentität (E-p12 a; identisch mit dem `candidateKey` der
 * Aufgabenart, soweit diese dieselbe Quelle hat): `reply:<messageId>`, `followup:<id>:<rev>`, `warmup:<stepId>`,
 * `seq:<enrollmentId>:<stepIndex>`, `comment|reaction:<radarItemId>`, `privacy:<personId>:<reopenCount>`,
 * `nurture:<personId>:<YYYY-Qn>`, `post:<contentItemId>`, `operation:<operationId>`. Rein und einstiegsunabhängig; das
 * Ergebnis besteht immer `ActionIdentity`. Unzulässige Bestandteile (Doppelpunkt, Leerraum, leer, zu lang, negative
 * oder gebrochene Zähler, falsches Quartal) → `RangeError`, ohne den Wert in die Meldung zu übernehmen.
 */
export const deriveActionIdentity: DeriveActionIdentity = (src) => {
  switch (src.kind) {
    case 'reply':
      return `reply:${part(src.inboundMessageId, 'inboundMessageId')}`;
    case 'followup':
      return `followup:${part(src.id, 'id')}:${count(src.rev, 'rev')}`;
    case 'warmup':
      return `warmup:${part(src.stepId, 'stepId')}`;
    case 'seq':
      return `seq:${part(src.enrollmentId, 'enrollmentId')}:${count(src.stepIndex, 'stepIndex')}`;
    case 'comment':
    case 'reaction':
      return `${src.kind}:${part(src.radarItemId, 'radarItemId')}`;
    case 'privacy':
      return `privacy:${part(src.personId, 'personId')}:${count(src.reopenCount, 'reopenCount')}`;
    case 'nurture': {
      if (!IDENTITY_QUARTER.test(src.quarter)) {
        throw new RangeError('deriveActionIdentity: ungültiges Quartal (quarter)');
      }
      return `nurture:${part(src.personId, 'personId')}:${src.quarter}`;
    }
    case 'post':
      return `post:${part(src.contentItemId, 'contentItemId')}`;
    case 'operation':
      return `operation:${part(src.operationId, 'operationId')}`;
  }
};

export interface ComplianceService {
  evaluate(tx: DbTx | null, ctx: DomainContext, i: OutboundCheck): Promise<ComplianceVerdict>;
  reserveOutbound(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    i: OutboundCheck & { holder: LedgerHolder; confirmations?: { not_promotional?: true } },
  ): Promise<
    DomainResult<{
      clipboardText: string | null;
      art14Appended: boolean;
      ledgerId: string;
      reused: boolean;
    }>
  >;
  capacityFor(
    tx: DbTx,
    ctx: DomainContext,
    a: {
      actionIdentity: string;
      connectedAccountId: string;
      actionKind: z.infer<typeof ActionKind>;
      day: string;
    },
  ): Promise<{
    allowed: boolean;
    reusable: boolean;
    reason?: 'account_day' | 'account_week' | 'outbound_daily_limit';
  }>;
  onIntentClassified(
    ctx: DomainContext,
    a: {
      messageId: string;
      contentRevision: number;
      optOut: boolean;
      consentSignal: boolean;
      confidenceBand: 'low' | 'medium' | 'high';
    },
  ): Promise<void>;
  getArt14(
    tx: DbTx | null,
    ctx: DomainContext,
    personId: PersonId,
  ): Promise<{
    status: 'pending' | 'provided' | 'overdue' | 'exempt_decision';
    dueBy: string;
  } | null>;
  getArt14Strategy(ctx: DomainContext): Promise<{
    strategy: 'undecided' | 'individual' | 'public_notice_exemption';
    decisionId: string | null;
  }>;
  isContactBlocked(
    tx: DbTx | null,
    ctx: DomainContext,
    personId: PersonId,
  ): Promise<boolean | null>;
}

/**
 * v11 (E-n Nr. 7): TP1 ruft in der Transaktion, die eine eingehende Nachricht (nicht SPAM) anlegt oder ihre
 * content_revision erhöht; schreibt nur den Befund optout_check (message_id, content_revision), wendet keine
 * Sperre an.
 */
export interface InboundMessageScreen {
  screen(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { messageId: string; contentRevision: number; text: string },
  ): Promise<void>;
}

/**
 * Lesezugriff auf den Einwilligungsstand für `person_get.consent` (C-E1, Plan Welle 2 E RW-E-1; Anbieter `compliance`,
 * W2-02). Liefert den Stand **des Aufrufers** zur Person: `status` wie `ComplianceVerdict.consent` und den Opt-out-Befund.
 * Fremde, unbekannte, eingeschränkte und gelöschte Personen sind ununterscheidbar der neutrale Wert
 * `{ status: 'not_recorded', optedOut: false }` (nie ein Wurf); die Einwilligung eines anderen Benutzers zur selben
 * Person zählt nicht; der Datenmodus des Kontexts trennt Live- und Demo-Daten.
 */
export interface ConsentStatusQuery {
  consentOf(
    ctx: DomainContext,
    personId: PersonId,
  ): Promise<{ status: ComplianceVerdict['consent']; optedOut: boolean }>;
}

/**
 * Quelle der Erhebung für den Art.-14-Hinweis (VB-Z10): `linkedin` (Import, Einfügen) oder `public_web` (Fund aus der KI-
 * Websuche der Zielfindung, „öffentlich zugängliche Quellen“). Eine Person kann beide haben.
 */
export type Art14CollectionSource = 'linkedin' | 'public_web';

/**
 * Meldet die Speicherung einer Person. Der Status ist mandantenweit je Person: die **früheste** Erhebung (`collectedAt`)
 * bestimmt die Frist (eine spätere ändert sie nie), die Quellen werden über alle Erfassungen **vereinigt**; bei der
 * Auswahl des Hinweises (`reserveOutbound`) zählt die vereinigte Quellenlage.
 */
export interface Art14Registrar {
  registerCollection(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: {
      personId: PersonId;
      collectedAt: Date;
      purpose: 'import' | 'target';
      source: Art14CollectionSource;
    },
  ): Promise<void>;
}
